import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import AssistantQuoteSummary from './AssistantQuoteSummary';
import { toLA_yyyy_mm_dd } from '../../utils/dateLA';
import { createAppointmentSafe } from '../../services/appointments/createAppointmentSafe';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface QuoteData {
  eventType: string;
  eventDate: string;
  city: string;
  appointmentType?: 'presencial' | 'llamada';
  hours?: number;
  addons?: string[];
  total?: number;
  breakdown?: any;
  pricing?: any;
}

interface Props {
  onClose: () => void;
  isOpen: boolean;
}

const EVENT_TYPES = {
  boda: 'Boda',
  quinceañera: 'Quinceañera',
  evento_corporativo: 'Evento Corporativo',
  sesion_familiar: 'Sesión Familiar',
  graduacion: 'Graduación',
};

export default function AssistantChatPanel({ isOpen, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: '¡Hola! 👋 Soy tu asistente virtual.\n\nPara ayudarte mejor, cuéntame sobre tu evento.\n\nPrimero, ¿qué tipo de evento necesitas?',
      timestamp: new Date(),
    },
  ]);

  const [step, setStep] = useState<
    | 'event_type'
    | 'date'
    | 'city'
    | 'appointment_type'
    | 'contact_name'
    | 'contact_email'
    | 'contact_phone'
    | 'confirm'
    | 'done'
  >('event_type');

  const [formData, setFormData] = useState({
    eventType: '',
    eventDate: '',
    city: '',
    appointmentType: '', // 'presencial' o 'llamada'
    name: '',
    email: '',
    phone: '',
  });

  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCheckingDate, setIsCheckingDate] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [bookedDates, setBookedDates] = useState<string[]>([]);
  const [showEventSelect, setShowEventSelect] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAppointmentType, setShowAppointmentType] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll cuando hay nuevos mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    loadBlockedAndBookedDates();
  }, []);

  const loadBlockedAndBookedDates = async () => {
    try {
      // Cargar fechas bloqueadas - la tabla calendar_blocks no tiene columna is_blocked
      // Todos los registros en calendar_blocks son fechas bloqueadas
      const { data: blocks } = await supabase
        .from('calendar_blocks')
        .select('date');

      const { data: appointments } = await supabase
        .from('appointments')
        .select('date')
        .neq('status', 'canceled');

      setBlockedDates(blocks?.map(b => b.date) || []);
      setBookedDates(appointments?.map(a => a.date) || []);
    } catch (err) {
      console.error('Error loading dates:', err);
    }
  };

  const addMessage = (role: 'user' | 'assistant', content: string) => {
    setMessages((prev) => [...prev, { role, content, timestamp: new Date() }]);
  };

  const checkAvailability = async (date: string): Promise<boolean> => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/assistant-quote`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'get_availability',
            data: { date },
          }),
        }
      );

      const result = await response.json();
      return result.available;
    } catch (error) {
      console.error('Error checking availability:', error);
      return false;
    }
  };

  const createAppointment = async () => {
    setIsCheckingDate(true);
    try {
      // Validar teléfono obligatorio para llamada o presencial
      if ((formData.appointmentType === 'llamada' || formData.appointmentType === 'presencial') && !formData.phone) {
        addMessage('assistant', '❌ El teléfono es obligatorio para citas de tipo llamada o presencial.\n\nPor favor proporciona tu número de teléfono.');
        setStep('contact_phone');
        setIsCheckingDate(false);
        return;
      }

      // Usar el wrapper seguro
      const result = await createAppointmentSafe({
        date: formData.eventDate!,
        time_start: '10:00',
        status: 'requested',
        client_name: formData.name,
        client_email: formData.email,
        client_phone: formData.phone,
        city: formData.city,
        event_type: formData.eventType,
        event_date: formData.eventDate,
        appointment_type: formData.appointmentType as 'call' | 'in_person' | 'online',
      });

      // Verificar resultado con ok
      if (!result.ok) {
        // Mostrar error real en consola y UI
        console.error('CREATE_APPOINTMENT_ERROR', result.error);
        addMessage('assistant', `❌ Error al crear cita: ${result.error}\n\nPor favor intenta de nuevo o contáctanos directamente.`);
        setStep('done');
        setIsCheckingDate(false);
        return;
      }

      // Éxito
      await new Promise((resolve) => setTimeout(resolve, 800));
      const confirmMessage = formData.appointmentType === 'presencial'
        ? `✅ ¡Cita confirmada!\n\n📅 Fecha: ${formData.eventDate}\n📍 Ciudad: ${formData.city}\n🤝 Tipo: Cita presencial\n📞 Teléfono: ${formData.phone}\n\nNos pondremos en contacto contigo pronto para coordinar el lugar y hora de la reunión.\n\n¡Gracias por confiar en nosotros! 📸✨`
        : `✅ ¡Cita confirmada!\n\n📅 Fecha: ${formData.eventDate}\n📍 Ciudad: ${formData.city}\n📞 Tipo: Llamada telefónica\n📱 Te llamaremos al: ${formData.phone}\n\nTe llamaremos para hablar sobre tu evento.\n\n¡Gracias por confiar en nosotros! 📸✨`;
      
      addMessage('assistant', confirmMessage);
      setStep('done');
    } catch (error: any) {
      // Capturar errores inesperados
      console.error('CREATE_APPOINTMENT_UNEXPECTED_ERROR', error);
      addMessage('assistant', `❌ Error inesperado: ${error.message || 'Error desconocido'}\n\nPor favor intenta de nuevo o contáctanos directamente.`);
      setStep('done');
    } finally {
      setIsCheckingDate(false);
    }
  };

  const handleEventTypeSelect = async (eventType: string) => {
    setFormData((prev) => ({ ...prev, eventType }));
    addMessage('user', eventType);

    await new Promise((resolve) => setTimeout(resolve, 500));
    addMessage('assistant', `Perfecto! ${eventType} 📸\n\nAhora, ¿para qué fecha necesitas el servicio?`);
    setStep('date');
  };

  const handleDateSelect = async () => {
    if (!selectedDate) return;

    const dateStr = toLA_yyyy_mm_dd(selectedDate);
    setFormData((prev) => ({ ...prev, eventDate: dateStr }));
    addMessage('user', dateStr);

    setIsCheckingDate(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/assistant-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'get_availability',
          data: { date: dateStr },
        }),
      });

      const result = await response.json();

      if (result.available) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        addMessage('assistant', `✅ ¡Perfecto! La fecha ${dateStr} está disponible.\n\n¿En qué ciudad será el evento?`);
        setStep('city');
      } else {
        addMessage('assistant', `❌ Lo siento, esa fecha no está disponible (${result.reason?.toLowerCase()}).\n\nPor favor selecciona otra fecha.`);
      }
    } catch (error) {
      console.error('Error checking date:', error);
      addMessage('assistant', '❌ Error al verificar la fecha. Por favor intenta de nuevo.');
    } finally {
      setIsCheckingDate(false);
    }
  };

  const handleCitySubmit = async () => {
    if (!inputValue.trim()) return;

    const city = inputValue.trim();
    setFormData((prev) => ({ ...prev, city }));
    addMessage('user', city);
    setInputValue('');

    await new Promise((resolve) => setTimeout(resolve, 500));
    addMessage('assistant', `Excelente! ${city} 📍\n\n¿Qué tipo de cita prefieres?`);
    setStep('appointment_type');
  };

  const handleAppointmentTypeSelect = async (type: 'presencial' | 'llamada') => {
    setFormData((prev) => ({ ...prev, appointmentType: type }));
    addMessage('user', type === 'presencial' ? '🤝 Cita presencial' : '📞 Llamada telefónica');

    await new Promise((resolve) => setTimeout(resolve, 500));
    const message = type === 'presencial' 
      ? 'Perfecto! Nos reuniremos en persona para mostrarte nuestro portafolio.\n\nPara confirmar tu cita, necesito algunos datos.\n\n¿Cuál es tu nombre completo?'
      : 'Perfecto! Te llamaremos para hablar sobre tu evento.\n\nPara confirmar tu cita, necesito algunos datos.\n\n¿Cuál es tu nombre completo?';
    
    addMessage('assistant', message);
    setStep('contact_name');
  };

  const handleNameSubmit = async () => {
    if (!inputValue.trim()) return;

    const name = inputValue.trim();
    setFormData((prev) => ({ ...prev, name }));
    addMessage('user', name);
    setInputValue('');

    await new Promise((resolve) => setTimeout(resolve, 500));
    addMessage('assistant', `Mucho gusto, ${name}! 😊\n\n¿Cuál es tu correo electrónico?`);
    setStep('contact_email');
  };

  const handleEmailSubmit = async () => {
    if (!inputValue.trim()) return;

    const email = inputValue.trim();
    
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      addMessage('assistant', '❌ Por favor ingresa un correo electrónico válido.');
      return;
    }

    setFormData((prev) => ({ ...prev, email }));
    addMessage('user', email);
    setInputValue('');

    await new Promise((resolve) => setTimeout(resolve, 500));
    addMessage('assistant', 'Perfecto! ✉️\n\n¿Cuál es tu número de teléfono?');
    setStep('contact_phone');
  };

  const handlePhoneSubmit = async () => {
    if (!inputValue.trim()) return;

    const phone = inputValue.trim();
    
    // Primero actualizar formData con el teléfono
    const updatedFormData = { ...formData, phone };
    setFormData(updatedFormData);
    
    addMessage('user', phone);
    setInputValue('');

    await new Promise((resolve) => setTimeout(resolve, 500));
    addMessage('assistant', '¡Excelente! 📱\n\nDéjame confirmar los datos de tu cita...');
    setStep('confirm');

    // Crear la cita con los datos actualizados
    await createAppointmentWithData(updatedFormData);
  };

  const createAppointmentWithData = async (data: typeof formData) => {
    setIsCheckingDate(true);
    try {
      // Validar teléfono obligatorio para llamada o presencial
      if ((data.appointmentType === 'llamada' || data.appointmentType === 'presencial') && !data.phone) {
        addMessage('assistant', '❌ El teléfono es obligatorio para citas de tipo llamada o presencial.\n\nPor favor proporciona tu número de teléfono.');
        setStep('contact_phone');
        setIsCheckingDate(false);
        return;
      }

      // Usar el wrapper seguro
      const result = await createAppointmentSafe({
        date: data.eventDate!,
        time_start: '10:00',
        status: 'requested',
        client_name: data.name,
        client_email: data.email,
        client_phone: data.phone,
        city: data.city,
        event_type: data.eventType,
        event_date: data.eventDate,
        appointment_type: data.appointmentType as 'call' | 'in_person' | 'online',
      });

      // Verificar resultado con ok
      if (!result.ok) {
        // Mostrar error real en consola y UI
        console.error('CREATE_APPOINTMENT_ERROR', result.error);
        addMessage('assistant', `❌ Error al crear cita: ${result.error}\n\nPor favor intenta de nuevo o contáctanos directamente.`);
        setStep('done');
        setIsCheckingDate(false);
        return;
      }

      // Éxito
      await new Promise((resolve) => setTimeout(resolve, 800));
      const confirmMessage = data.appointmentType === 'presencial'
        ? `✅ ¡Cita confirmada!\n\n📅 Fecha: ${data.eventDate}\n📍 Ciudad: ${data.city}\n🤝 Tipo: Cita presencial\n📞 Teléfono: ${data.phone}\n\nNos pondremos en contacto contigo pronto para coordinar el lugar y hora de la reunión.\n\n¡Gracias por confiar en nosotros! 📸✨`
        : `✅ ¡Cita confirmada!\n\n📅 Fecha: ${data.eventDate}\n📍 Ciudad: ${data.city}\n📞 Tipo: Llamada telefónica\n📱 Te llamaremos al: ${data.phone}\n\nTe llamaremos para hablar sobre tu evento.\n\n¡Gracias por confiar en nosotros! 📸✨`;
      
      addMessage('assistant', confirmMessage);
      setStep('done');
    } catch (error: any) {
      // Capturar errores inesperados
      console.error('CREATE_APPOINTMENT_UNEXPECTED_ERROR', error);
      addMessage('assistant', `❌ Error inesperado: ${error.message || 'Error desconocido'}\n\nPor favor intenta de nuevo o contáctanos directamente.`);
      setStep('done');
    } finally {
      setIsCheckingDate(false);
    }
  };

  const handleSend = () => {
    if (!inputValue.trim() || isCheckingDate) return;

    const userMessage = inputValue.trim().toLowerCase();

    // Detectar si pide ver galería/portafolio (pero NO si es una ciudad)
    // Excluir si contiene "ca" como parte de una ciudad de California
    const isCityWithCA = /\b(ca|california)\b/.test(userMessage) && 
                         (userMessage.includes('cloverdale') || 
                          userMessage.includes('san') || 
                          userMessage.includes('los') || 
                          userMessage.includes('sacramento') ||
                          userMessage.includes('fresno') ||
                          userMessage.includes('oakland') ||
                          userMessage.includes('bakersfield') ||
                          userMessage.includes('anaheim') ||
                          userMessage.includes('riverside') ||
                          userMessage.includes('stockton') ||
                          userMessage.includes('irvine') ||
                          userMessage.includes('fremont') ||
                          userMessage.includes('modesto') ||
                          userMessage.includes('santa') ||
                          userMessage.includes('city') ||
                          userMessage.includes('county') ||
                          /\d{5}/.test(userMessage)); // código postal

    if (
      !isCityWithCA &&
      (userMessage.includes('galería') ||
      userMessage.includes('galeria') ||
      userMessage.includes('portafolio') ||
      userMessage.includes('trabajos') ||
      userMessage.includes('fotos') ||
      userMessage.includes('imágenes') ||
      userMessage.includes('imagenes') ||
      userMessage.includes('ver') ||
      userMessage.includes('muestra'))
    ) {
      addMessage('user', inputValue);
      setInputValue('');
      addMessage('assistant', '¡Claro! Te mostraré nuestros trabajos... 📸');
      
      setTimeout(() => {
        onClose();
        const servicesSection = document.getElementById('services-section');
        if (servicesSection) {
          servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 1000);
      return;
    }

    // Manejar según el paso actual
    if (step === 'city') {
      handleCitySubmit();
    } else if (step === 'contact_name') {
      handleNameSubmit();
    } else if (step === 'contact_email') {
      handleEmailSubmit();
    } else if (step === 'contact_phone') {
      handlePhoneSubmit();
    } else {
      addMessage('user', inputValue);
      setInputValue('');
      addMessage('assistant', 'Por favor sigue los pasos para agendar tu cita. 😊');
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toLA_yyyy_mm_dd(tomorrow);
  };

  const isDateDisabled = (dateStr: string) => {
    return blockedDates.includes(dateStr) || bookedDates.includes(dateStr);
  };

  return (
    <div className={`fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-customer-service-2-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-semibold">Asistente Virtual</h3>
            <p className="text-xs text-white/80">En línea</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded-lg transition-colors"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                msg.role === 'user'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-gray-800 border border-gray-200'
              }`}
            >
              <p className="text-sm whitespace-pre-line">{msg.content}</p>
              <p className="text-xs mt-1 opacity-70">
                {msg.timestamp.toLocaleTimeString('es-MX', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        ))}
        {/* Elemento invisible para el auto-scroll */}
        <div ref={messagesEndRef} />
        {/* Event Type Selection */}
        {step === 'event_type' && (
          <div className="space-y-2">
            {['Boda', 'Quinceañera', 'Evento Corporativo', 'Sesión Familiar', 'Graduación'].map((type) => (
              <button
                key={type}
                onClick={() => handleEventTypeSelect(type)}
                className="w-full bg-white hover:bg-teal-50 border-2 border-gray-200 hover:border-teal-500 rounded-xl p-3 text-left transition-all duration-200 text-sm font-medium text-gray-700 hover:text-teal-700"
              >
                {type}
              </button>
            ))}
          </div>
        )}

        {/* Date Selection */}
        {step === 'date' && (
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <input
              type="date"
              value={selectedDate ? toLA_yyyy_mm_dd(selectedDate) : ''}
              onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value + 'T12:00:00') : null)}
              min={toLA_yyyy_mm_dd(new Date(Date.now() + 86400000))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
            <button
              onClick={handleDateSelect}
              disabled={!selectedDate || isCheckingDate}
              className="w-full mt-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white py-2 rounded-lg transition-colors text-sm font-medium whitespace-nowrap"
            >
              {isCheckingDate ? 'Verificando...' : 'Confirmar fecha'}
            </button>
          </div>
        )}

        {/* Appointment Type Selection */}
        {step === 'appointment_type' && (
          <div className="space-y-3">
            <button
              onClick={() => handleAppointmentTypeSelect('presencial')}
              className="w-full bg-white hover:bg-teal-50 border-2 border-gray-200 hover:border-teal-500 rounded-xl p-4 text-left transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">🤝 Cita presencial</h4>
                  <p className="text-xs text-gray-600 mt-1">Nos reunimos en persona para mostrarte nuestro portafolio completo</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleAppointmentTypeSelect('llamada')}
              className="w-full bg-white hover:bg-teal-50 border-2 border-gray-200 hover:border-teal-500 rounded-xl p-4 text-left transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">📞 Llamada telefónica</h4>
                  <p className="text-xs text-gray-600 mt-1">Te llamamos para hablar sobre tu evento y resolver tus dudas</p>
                </div>
              </div>
            </button>
          </div>
        )}

        {isCheckingDate && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl px-4 py-2.5 border border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      {(step === 'city' || step === 'contact_name' || step === 'contact_email' || step === 'contact_phone') && (
        <div className="p-4 bg-white border-t border-gray-200">
          <div className="flex gap-2">
            <input
              type={step === 'contact_email' ? 'email' : step === 'contact_phone' ? 'tel' : 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={
                step === 'city' ? 'Escribe la ciudad...' :
                step === 'contact_name' ? 'Tu nombre completo...' :
                step === 'contact_email' ? 'tu@email.com' :
                'Tu número de teléfono...'
              }
              disabled={isCheckingDate}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || isCheckingDate}
              className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap"
            >
              <i className="ri-send-plane-fill"></i>
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="p-4 bg-white border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2.5 rounded-xl transition-colors text-sm font-medium whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}
